<template>
	<div class="facts-subview">
		<table v-if="groups.length > 0" class="factSheet">
			<tbody v-for="g in groups" :key="g.id" :id="'fact_group_'+g.id">
				<template v-for="(item, idx) in g.facts">
					<template v-if="idx === 0 || (g.opener ? openers[g.id] : true)">
						<tr :class="'factType'+item.type">
							<td :rowspan="item.info ? 2 : 1" class="factIcon">
								<template v-if="item.icon">
									<it-link :to="item.iconLink">
										<sym-icon :name="item.icon" :title="item.iconTitle"></sym-icon>
									</it-link>
								</template>
							</td>
							<td class="factTitle" :colspan="(item.value || idx===0 && g.opener) ? 1 : 2" v-html="item.title"></td>
							<td v-if="idx===0 && g.opener" class="factOpener"><more-opener v-model="openers[g.id]"></more-opener></td>
							<td v-else-if="item.value" class="factValue" v-html="item.value"></td>
						</tr>
						<tr v-if="item.info" :class="'factType'+item.type">
							<td colspan="2" class="factInfo">{{ item.info }}</td>
						</tr>
					</template>
				</template>
			</tbody>
			<tbody><tr class="factSheetEnd"><td class="factIconEnd"></td><td colspan="2" class="factTitleEnd"></td></tr></tbody>
		</table>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, reactive, watch} from "vue";
import {injectEnv} from "@fd/Environment.ts";
import {ContextKey, getIntValue, injectContext} from "@t/fd/AppContext.ts";
import {Fact, FactsResponse} from "@t/customer/Fact.ts";
import {SrcType} from "@t/meta/SourceType.ts";
import ItLink from "@/router/ItLink.vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import MoreOpener from "@fd/helpers/MoreOpener.vue";

const env = injectEnv();
const ctx = injectContext();
const sourceType = computed<number>(() => getIntValue(ctx.value, ContextKey.SRC_TYPE));
const sourceId = computed<string|number>(() => getIntValue(ctx.value, ContextKey.SRC_ID));

const facts = reactive<Fact[]>([]);
const width = ref<number>(0);
const openers = reactive<{[key:string]:boolean}>({});

interface FactGroup {
	id: string;
	/** Emit opener on the first item */
	opener: boolean;
	facts: Fact[];
}

const groups = computed<FactGroup[]>(() => {
	const g:FactGroup[] = [];
	let groupId = "!";
	for (let f of facts) {
		const currentGroupId = f.group ?? "";
		if (currentGroupId !== groupId) {
			g.push({ id: currentGroupId, facts: [], opener: false });
			groupId = currentGroupId;
		}
		g[g.length - 1].facts.push(f);
	}

	for (let grp of g) {
		grp.opener = grp.facts.length > 1 && grp.facts[0].type === "Head";
		if (grp.opener) {
			openers[grp.id] = !grp.facts[0].closed;
		}
	}
	return g;
});

watch([sourceId, sourceType], async () => {
	if (sourceId.value && sourceType.value === SrcType.PARTY) {
		const resp = await env.net.fetch("api/parties/" + sourceId.value + "/facts");
		if (resp.ok) {
			const fr = await resp.json() as FactsResponse;
			facts.length = 0;
			facts.push(...fr.facts);
			width.value = fr.width;
		}
	}
}, { immediate: true });

</script>

<style>
.facts-subview {
	padding-top: 8px;
}
</style>